/**
 * ════════════════════════════════════════════════════════════════════
 *  kingz-fbt.js — Native "Frequently Bought Together" behavior (PORTABLE)
 *  Replaces the codeblackbelt app widget across all Kingz stores.
 *
 *  Portable cart integration — auto-detects the store's cart UI:
 *    1. US (Impulse/custom):  window.kingzSlideCart slide-cart drawer
 *    2. UK / EU (Dawn-based): <cart-notification> / <cart-drawer> custom
 *       elements (uses the sections rendering API + PUB_SUB cartUpdate)
 *    3. Fallbacks: window.updateKingzCart(), else redirect to /cart
 *
 *  Behaviour:
 *    - Recomputes the running total as items are checked/unchecked or a
 *      variant is changed.
 *    - "Add selected to cart" adds all checked items in ONE /cart/add.js
 *      call, then opens whichever cart UI the theme provides.
 *    - If a product has no curated data yet (data-needs-fallback), it
 *      fetches Shopify native related recommendations so the block is
 *      never empty.
 * ════════════════════════════════════════════════════════════════════
 */
(function () {
  'use strict';

  function initSection(section) {
    if (section.__fbtInit) return;
    section.__fbtInit = true;

    var list = section.querySelector('.kingz-fbt__list');
    var totalEl = section.querySelector('[data-fbt-total]');
    var regularEl = section.querySelector('[data-fbt-regular]');
    var savingsEl = section.querySelector('[data-fbt-savings]');
    var savingsSpacer = section.querySelector('[data-fbt-savings-spacer]');
    var addBtn = section.querySelector('[data-fbt-add]');
    var errEl = section.querySelector('[data-fbt-error]');
    var maxItems = parseInt(section.getAttribute('data-max-items'), 10) || 3;
    // Bundle discount % (matches the Shopify automatic "Buy X Get Y" discount).
    // The discount applies to the ENTIRE bundle (gi + accessories together),
    // making the complete kit 15% off to maximize conversion.
    var discountPct = parseFloat(section.getAttribute('data-bundle-discount')) || 0;
    // Minimum number of items that must be selected before the bundle saving is
    // shown. This mirrors the Shopify automatic discount's "Minimum quantity of
    // items" rule so the product-page total never promises a discount that
    // checkout won't apply.
    var minItems = parseInt(section.getAttribute('data-bundle-min-items'), 10) || 3;

    // ── Money formatting (mirror Shopify's money filter) ────────────
    var moneyFormat = '${{amount}}';
    try {
      var mfEl = section.querySelector('[data-fbt-money-format]');
      if (mfEl) moneyFormat = JSON.parse(mfEl.textContent);
    } catch (e) { /* keep default */ }
    // Some currency/translation apps (e.g. Transcy) inject HTML markup into
    // shop.money_format (e.g. "<span class=transcy-money>€{{amount}} EUR</span>").
    // Because we render the total with textContent, that markup would show as
    // literal text. Strip tags & decode entities so we keep a clean template.
    (function sanitizeMoneyFormat() {
      if (typeof moneyFormat !== 'string' || moneyFormat.indexOf('<') === -1) return;
      var tmp = document.createElement('div');
      tmp.innerHTML = moneyFormat;
      var clean = (tmp.textContent || tmp.innerText || '').trim();
      if (clean && clean.indexOf('{{') !== -1) moneyFormat = clean;
    })();

    function formatMoney(cents) {
      cents = typeof cents === 'number' ? cents : parseInt(cents, 10) || 0;
      function withCommas(n) { return n.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,'); }
      function fmt(precision, thousands) {
        var value = (cents / 100.0).toFixed(precision);
        var parts = value.split('.');
        var dollars = withCommas(parts[0]);
        if (thousands) dollars = dollars.replace(/,/g, thousands);
        return parts[1] ? dollars + '.' + parts[1] : dollars;
      }
      return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, function (m, token) {
        switch (token) {
          case 'amount': return fmt(2);
          case 'amount_no_decimals': return fmt(0);
          case 'amount_with_comma_separator': return fmt(2).replace(/,/g, '.').replace(/\.(\d{2})$/, ',$1');
          case 'amount_no_decimals_with_comma_separator': return fmt(0, '.');
          default: return fmt(2);
        }
      });
    }

    // ── Build required option pickers (Size / Color) for an item ────
    // For multi-variant products the Liquid emits variant data as JSON but
    // NO pre-selected variant. We build one <select> per option here, each
    // defaulting to a "Choose <Option>" placeholder, so the customer MUST
    // pick every option (e.g. Size + Color) before the item can be added.
    function setupItemVariants(item) {
      if (item.__fbtVariantsInit) return;
      var vjson = item.querySelector('[data-fbt-variants]');
      if (!vjson) return; // single-variant span or legacy select — nothing to build
      var variants, names, values;
      try {
        variants = JSON.parse(vjson.textContent);
        names = JSON.parse(item.querySelector('[data-fbt-option-names]').textContent);
        values = JSON.parse(item.querySelector('[data-fbt-option-values]').textContent);
      } catch (e) { return; }
      if (!variants || !variants.length) return;
      item.__fbtVariants = variants;
      item.__fbtVariantsInit = true;

      var container = item.querySelector('[data-fbt-options]');
      if (!container) return;
      container.innerHTML = '';

      names.forEach(function (name, i) {
        // Labelled field wrapper: small uppercase label (e.g. SIZE) above a
        // compact styled <select>, laid out inline via .kingz-fbt__options flex.
        var field = document.createElement('div');
        field.className = 'kingz-fbt__field';

        var label = document.createElement('label');
        label.className = 'kingz-fbt__field-label';
        label.textContent = name; // e.g. "Size", "Color"

        var sel = document.createElement('select');
        sel.className = 'kingz-fbt__option';
        sel.setAttribute('data-fbt-option', i);
        sel.setAttribute('aria-label', 'Choose ' + name);
        var ph = document.createElement('option');
        ph.value = '';
        ph.textContent = 'Select';
        ph.disabled = true;
        ph.selected = true;
        sel.appendChild(ph);
        (values[i] || []).forEach(function (val) {
          var o = document.createElement('option');
          o.value = val;
          o.textContent = val;
          sel.appendChild(o);
        });
        sel.addEventListener('change', function () { resolveItemVariant(item); });

        field.appendChild(label);
        field.appendChild(sel);
        container.appendChild(field);
      });

      resolveItemVariant(item); // initialise price display ("From …")
    }

    // Resolve the exact variant from the currently-selected options.
    // Sets item.__fbtResolved to the matching variant (or null) and updates
    // the item's price display.
    function resolveItemVariant(item) {
      var variants = item.__fbtVariants;
      if (!variants) return null;
      var selects = item.querySelectorAll('[data-fbt-option]');
      var chosen = [];
      var complete = true;
      selects.forEach(function (s) {
        if (!s.value) complete = false;
        chosen.push(s.value);
      });

      var match = null;
      if (complete) {
        for (var i = 0; i < variants.length; i++) {
          var v = variants[i];
          var ok = true;
          for (var j = 0; j < chosen.length; j++) {
            if ((v.options[j] || '') !== chosen[j]) { ok = false; break; }
          }
          if (ok) { match = v; break; }
        }
      }
      item.__fbtResolved = match || null;

      var pd = item.querySelector('[data-fbt-price-display]');
      if (pd) {
        if (match) {
          pd.textContent = formatMoney(match.price) + (match.available ? '' : ' (sold out)');
          pd.classList.toggle('opacity-60', !match.available);
        } else {
          var min = variants[0].price;
          for (var k = 1; k < variants.length; k++) { if (variants[k].price < min) min = variants[k].price; }
          pd.textContent = 'From ' + formatMoney(min);
          pd.classList.remove('opacity-60');
        }
      }
      recomputeTotal();
      return match;
    }

    // ── Read the active variant id + price (cents) from an item ─────
    // Returns { id, price, disabled, needsChoice }. needsChoice=true means
    // the customer still has to pick Size/Color for a multi-variant item.
    function itemVariant(item) {
      // 1) New option-picker items (multi-variant complements).
      if (item.__fbtVariants) {
        var m = item.__fbtResolved;
        if (m) return { id: m.id, price: m.price, disabled: !m.available, needsChoice: false };
        return { id: null, price: 0, disabled: true, needsChoice: true };
      }
      // 2) Legacy combined <select> (used by the current product row).
      var sel = item.querySelector('[data-fbt-variant-select]');
      if (sel) {
        var opt = sel.options[sel.selectedIndex];
        return { id: sel.value, price: parseInt(opt.getAttribute('data-price'), 10) || 0, disabled: opt.disabled, needsChoice: false };
      }
      // 3) Single-variant span.
      var span = item.querySelector('[data-fbt-price]');
      if (span) {
        return { id: span.getAttribute('data-fbt-variant'), price: parseInt(span.getAttribute('data-fbt-price'), 10) || 0, disabled: false, needsChoice: false };
      }
      return { id: null, price: 0, disabled: true, needsChoice: false };
    }

    function itemName(item) {
      var n = item.querySelector('.kingz-fbt__name');
      return n ? (n.textContent || '').replace(/\(this item\)/i, '').trim() : 'this item';
    }

    // Briefly outline an item's option pickers so the customer sees which
    // ones still need a Size/Color choice.
    function highlightItem(item) {
      var opts = item.querySelector('[data-fbt-options]');
      if (!opts) return;
      opts.querySelectorAll('select').forEach(function (s) {
        if (!s.value) {
          s.classList.add('kingz-fbt__option--error');
          s.addEventListener('change', function once() {
            s.classList.remove('kingz-fbt__option--error');
            s.removeEventListener('change', once);
          });
        }
      });
    }

    function recomputeTotal() {
      var total = 0;            // regular (pre-discount) total of all checked items
      var checkedCount = 0;     // number of checked items with a resolved price
      var complementChecked = 0;
      section.querySelectorAll('[data-fbt-item]').forEach(function (item) {
        var check = item.querySelector('[data-fbt-check]');
        if (check && check.checked) {
          var v = itemVariant(item);
          if (!v.needsChoice) {
            total += v.price; // unresolved items don't count yet
            checkedCount++;
            // Count complement items to ensure we have a real bundle (gi + accessories)
            if (item.hasAttribute('data-fbt-complement')) {
              complementChecked++;
            }
          }
        }
      });

      // A saving only materializes when (a) at least one accessory is bundled
      // with the main product AND (b) the total number of selected items meets
      // the Shopify discount's minimum-quantity rule. Gating on minItems keeps
      // the product-page total in sync with what checkout will actually apply.
      var savings = 0;
      if (discountPct > 0 && complementChecked > 0 &&
          checkedCount > complementChecked && checkedCount >= minItems) {
        savings = Math.round(total * (discountPct / 100));
      }
      var discounted = total - savings;

      if (savings > 0) {
        if (regularEl) { regularEl.textContent = formatMoney(total); regularEl.classList.remove('hidden'); }
        if (totalEl) totalEl.textContent = formatMoney(discounted);
        if (savingsEl) {
          savingsEl.textContent = 'You save ' + formatMoney(savings) + ' (' + discountPct + '% off entire bundle)';
          savingsEl.classList.remove('hidden');
        }
        if (savingsSpacer) savingsSpacer.classList.add('hidden');
      } else {
        if (regularEl) { regularEl.textContent = ''; regularEl.classList.add('hidden'); }
        if (totalEl) totalEl.textContent = formatMoney(total);
        if (savingsEl) { savingsEl.textContent = ''; savingsEl.classList.add('hidden'); }
        if (savingsSpacer) savingsSpacer.classList.remove('hidden');
      }
    }

    // ── Wire events (delegated so JS-added fallback rows work too) ──
    section.addEventListener('change', function (e) {
      if (e.target.matches('[data-fbt-check]') || e.target.matches('[data-fbt-variant-select]')) {
        recomputeTotal();
      }
    });

    // ── Portable "open cart" after a successful add ─────────────────
    function openCartUI(response, items) {
      // Always notify any custom listeners.
      document.dispatchEvent(new CustomEvent('kingz:cart-updated'));

      // 1) US slide-cart drawer.
      if (window.kingzSlideCart && typeof window.kingzSlideCart.open === 'function') {
        if (typeof window.kingzSlideCart.fetchCart === 'function') window.kingzSlideCart.fetchCart();
        window.kingzSlideCart.open();
        return;
      }

      // 2) Dawn cart-notification / cart-drawer (UK / EU).
      var cartEl = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cartEl && typeof cartEl.renderContents === 'function' && response && response.sections) {
        // renderContents expects a single product id for its per-product selector.
        if (!response.id && items && items.length) response.id = items[items.length - 1].id;
        try { if (typeof cartEl.setActiveElement === 'function') cartEl.setActiveElement(document.activeElement); } catch (e) { /* ignore */ }
        try {
          cartEl.renderContents(response);
        } catch (e) {
          // If rendering fails for any reason, fall through to reload.
          window.location = cartUrl();
          return;
        }
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
          try { window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'kingz-fbt', cartData: response }); } catch (e) { /* ignore */ }
        }
        return;
      }

      // 3) Legacy custom hook.
      if (typeof window.updateKingzCart === 'function') { window.updateKingzCart(); return; }

      // 4) Last resort — go to the cart page so the user still sees the items.
      window.location = cartUrl();
    }

    function cartRoot() {
      return ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/').replace(/\/$/, '');
    }
    function cartUrl() { return cartRoot() + '/cart'; }

    // ── Add selected to cart ────────────────────────────────────────
    addBtn && addBtn.addEventListener('click', function () {
      if (errEl) { errEl.classList.add('hidden'); errEl.textContent = ''; }
      var items = [];
      var needChoice = [];   // checked items missing a Size/Color selection
      var soldOut = [];      // checked items whose chosen variant is unavailable
      section.querySelectorAll('[data-fbt-item]').forEach(function (item) {
        var check = item.querySelector('[data-fbt-check]');
        if (!check || !check.checked) return;
        var v = itemVariant(item);
        if (v.needsChoice) { needChoice.push(itemName(item)); highlightItem(item); return; }
        if (!v.id || v.disabled) { soldOut.push(itemName(item)); return; }
        items.push({ id: Number(v.id), quantity: 1 });
      });

      // Block the add if any selected item still needs Size/Color chosen.
      if (needChoice.length) {
        showError('Please choose options for: ' + needChoice.join(', ') + '.');
        return;
      }
      if (soldOut.length && !items.length) {
        showError('Selected item is sold out: ' + soldOut.join(', ') + '.');
        return;
      }
      if (!items.length) {
        showError('Please select at least one item.');
        return;
      }
      addBtn.disabled = true;
      addBtn.setAttribute('aria-busy', 'true');
      var original = addBtn.textContent;
      addBtn.textContent = 'Adding…';

      // Ask the theme's cart UI which sections it needs re-rendered
      // (Dawn cart-notification / cart-drawer). Harmless on US theme.
      var body = { items: items };
      var cartEl = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cartEl && typeof cartEl.getSectionsToRender === 'function') {
        try {
          body.sections = cartEl.getSectionsToRender().map(function (s) { return s.id; });
          body.sections_url = window.location.pathname;
        } catch (e) { /* ignore — plain add */ }
      }

      fetch(cartRoot() + '/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      })
        .then(function (r) { if (!r.ok) return r.json().then(function (j) { throw new Error(j.description || 'Add to cart failed'); }); return r.json(); })
        .then(function (response) { openCartUI(response, items); })
        .catch(function (err) { showError(err.message || 'Something went wrong. Please try again.'); })
        .finally(function () {
          addBtn.disabled = false;
          addBtn.removeAttribute('aria-busy');
          addBtn.textContent = original;
        });
    });

    function showError(msg) {
      if (errEl) { errEl.textContent = msg; errEl.classList.remove('hidden'); }
      else { console.error('[Kingz FBT]', msg); }
    }

    // ── Fallback: fetch native related recs if no curated data ──────
    // Mirrors the curated markup: single-variant products show a price;
    // multi-variant products emit variant/option JSON so setupItemVariants()
    // can build required Size/Color pickers (same as curated items).
    function buildFallbackRow(p) {
      var hasVariants = p.variants && p.variants.length;
      if (!hasVariants) return null;
      // Determine whether this is truly multi-variant (real options).
      var optionNames = (p.options || []).map(function (o) {
        return (o && typeof o === 'object') ? o.name : o; // Ajax API returns strings; be defensive
      }).filter(function (n) { return n && n.toLowerCase() !== 'title'; });
      var multi = optionNames.length > 0 && p.variants.length > 1;

      var li = document.createElement('li');
      li.className = 'kingz-fbt__item flex items-center gap-3 p-2 border border-primary-section-border rounded';
      li.setAttribute('data-fbt-item', '');
      // Fallback rows are always complements (recommended add-ons) → discountable.
      li.setAttribute('data-fbt-complement', '');
      var img = p.featured_image ? ('<img src="' + p.featured_image + '&width=120" loading="lazy" width="60" height="60" class="w-[60px] h-[60px] object-cover rounded" alt="' + escapeHtml(p.title) + '">') : '';
      // Note: no per-item "Save X%" pill — the single bundle badge at the top
      // communicates the discount. Repeating it on every row was visual clutter.

      var meta;
      if (!multi) {
        // Single-variant: pick first available (or first) and show price.
        var variant = null;
        for (var i = 0; i < p.variants.length; i++) { if (p.variants[i].available) { variant = p.variants[i]; break; } }
        if (!variant) variant = p.variants[0];
        meta =
          '<a href="' + p.url + '" class="kingz-fbt__name text-sm font-medium truncate block hover:underline">' + escapeHtml(p.title) + '</a>' +
          '<span class="kingz-fbt__price text-sm block" data-fbt-price="' + variant.price + '" data-fbt-variant="' + variant.id + '">' + formatMoney(variant.price) + '</span>';
      } else {
        // Multi-variant: emit JSON blobs the picker builder consumes.
        var variantData = p.variants.map(function (v) {
          var opts = v.options || [v.option1, v.option2, v.option3];
          return { id: v.id, available: !!v.available, price: v.price, options: [opts[0] || null, opts[1] || null, opts[2] || null] };
        });
        var optionValues = optionNames.map(function (_, idx) {
          var seen = {}, arr = [];
          variantData.forEach(function (v) {
            var val = v.options[idx];
            if (val != null && !seen[val]) { seen[val] = 1; arr.push(val); }
          });
          return arr;
        });
        meta =
          '<a href="' + p.url + '" class="kingz-fbt__name text-sm font-medium truncate block hover:underline">' + escapeHtml(p.title) + '</a>' +
          '<script type="application/json" data-fbt-variants>' + JSON.stringify(variantData) + '<\/script>' +
          '<script type="application/json" data-fbt-option-names>' + JSON.stringify(optionNames) + '<\/script>' +
          '<script type="application/json" data-fbt-option-values>' + JSON.stringify(optionValues) + '<\/script>' +
          '<div class="kingz-fbt__options flex flex-wrap gap-1.5 mt-1" data-fbt-options><\/div>' +
          '<span class="kingz-fbt__price text-sm block mt-1" data-fbt-price-display><\/span>';
      }

      li.innerHTML =
        '<input type="checkbox" class="kingz-fbt__check shrink-0 w-5 h-5" data-fbt-check aria-label="Add ' + escapeHtml(p.title) + '">' +
        '<a href="' + p.url + '" class="kingz-fbt__thumb shrink-0">' + img + '</a>' +
        '<div class="kingz-fbt__meta min-w-0 flex-1">' + meta + '</div>';
      return li;
    }

    function escapeHtml(s) { return String(s).replace(/[&<>"']/g, function (c) { return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]; }); }

    if (section.getAttribute('data-needs-fallback') === 'true') {
      var url = section.getAttribute('data-recommend-url');
      // Ajax API returns JSON at /recommendations/products.json — normalize URL
      var jsonUrl = url.replace('/recommendations/products', '/recommendations/products.json');
      if (jsonUrl.indexOf('.json') === -1) jsonUrl = url; // safety
      fetch(jsonUrl, { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : { products: [] }; })
        .then(function (data) {
          var added = 0;
          (data.products || []).forEach(function (p) {
            if (added >= maxItems) return;
            if (!p.available) return;
            var row = buildFallbackRow(p);
            if (row) { list.appendChild(row); setupItemVariants(row); added++; }
          });
          recomputeTotal();
        })
        .catch(function () { /* leave block with just the current product */ });
    }

    // Build required option pickers for every curated (server-rendered) item.
    section.querySelectorAll('[data-fbt-item]').forEach(function (item) {
      setupItemVariants(item);
    });

    recomputeTotal();
  }

  function initAll() {
    document.querySelectorAll('.kingz-fbt[data-section-id]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  // Re-init on Shopify theme editor section load
  document.addEventListener('shopify:section:load', function (e) {
    var s = e.target.querySelector ? e.target.querySelector('.kingz-fbt[data-section-id]') : null;
    if (s) initSection(s);
  });
})();
